import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileSearch,
  FileText,
  LoaderCircle,
  MessageSquareText,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { ArgentineDateInput, FeedbackBanner } from "@tribunalea/ui";
import { CUIJ_PATTERN, formatCuij } from "@tribunalea/shared";
import { nextIsoDate } from "../lib/interest-dates.js";

const MAX_SENTENCE_FILES = 3;
const MAX_SENTENCE_BYTES = 15 * 1024 * 1024;

const RATE_NAMES = {
  0: "Banco Nación Activa",
  1: "Libre 36 meses",
  2: "Libre 60 meses",
  3: "Libre 72 meses · Ley 9516",
  4: "Banco Nación Pasiva",
  5: "Ley 4087",
  6: "Unidad de Valor Adquisitivo (UVA)",
};

function todayInput() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Mendoza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function displayDocumentDate(value) {
  if (!value) return "Fecha no informada";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeZone: "America/Argentina/Mendoza",
  }).format(date);
}

function apiDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function inputDate(value) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [day, month, year] = value.split("/");
  return `${year}-${month}-${day}`;
}

function optionValue(rule) {
  return rule.calculationType === "rate"
    ? `rate:${rule.rateCode}`
    : rule.calculationType;
}

function outcomeDebt(debt, capital) {
  return {
    id: debt.id,
    label: debt.label,
    capital: Number(capital),
    manualTranches: debt.rules.map((rule) => ({
      calculationType: rule.calculationType,
      ...(rule.calculationType === "rate"
        ? { rateCode: Number(rule.rateCode) }
        : {}),
      from: apiDate(rule.from),
      to: apiDate(rule.to),
    })),
  };
}

function analysisDebts(analysis) {
  return (analysis.debts || []).map((debt) => ({
    ...debt,
    includeAwarded:
      Number(debt.awardedCapital ?? debt.capital) > 0,
    includeRejected:
      Number(debt.rejectedCapital) > 0,
    claimedCapital: debt.claimedCapital == null
      ? ""
      : String(debt.claimedCapital),
    awardedCapital: (debt.awardedCapital ?? debt.capital) == null
      ? ""
      : String(debt.awardedCapital ?? debt.capital),
    rejectedCapital: debt.rejectedCapital == null
      ? ""
      : String(debt.rejectedCapital),
    rules: debt.resolvedRules.map((rule) => ({
      id: crypto.randomUUID(),
      calculationType: rule.calculationType,
      rateCode: rule.rateCode,
      from: rule.from,
      to:
        analysis.requiresFirstInstanceJudgmentDate
        && rule.to === analysis.calculationDate
          ? ""
          : rule.to,
      rationale: rule.rationale,
    })),
  }));
}

function savedSentenceDebts(draftInput, analysis, result) {
  const analyzedById = new Map(
    (analysis?.debts || []).map((debt) => [debt.id, debt]),
  );
  const resultById = new Map(
    (result?.debts || []).map((debt) => [debt.sourceDebtId || debt.id, debt]),
  );
  const awardedById = new Map(
    (draftInput?.outcomes || [])
      .find(({ key }) => key === "awarded")?.debts
      ?.map((debt) => [debt.id, debt]) || [],
  );
  const rejectedById = new Map(
    (draftInput?.outcomes || [])
      .find(({ key }) => key === "rejected")?.debts
      ?.map((debt) => [debt.id, debt]) || [],
  );
  const editableDebts = draftInput?.debts || (analysis?.debts || []).map(
    (debt) => {
      const awarded = awardedById.get(debt.id);
      const rejected = rejectedById.get(debt.id);
      return {
        ...debt,
        includeAwarded: Boolean(awarded),
        includeRejected: Boolean(rejected),
        awardedCapital: awarded?.capital ?? debt.awardedCapital ?? debt.capital,
        rejectedCapital: rejected?.capital ?? debt.rejectedCapital,
        manualTranches: awarded?.manualTranches || rejected?.manualTranches,
      };
    },
  );
  return editableDebts.map((debt) => {
    const analyzed = analyzedById.get(debt.id) || {};
    const savedResult = resultById.get(debt.id) || {};
    return {
      ...analyzed,
      ...savedResult,
      ...debt,
      includeAwarded: debt.includeAwarded ?? Number(
        debt.awardedCapital ?? debt.capital,
      ) > 0,
      includeRejected: debt.includeRejected ?? Number(debt.rejectedCapital) > 0,
      claimedCapital: String(
        debt.claimedCapital ?? analyzed.claimedCapital ?? "",
      ),
      awardedCapital: String(
        debt.awardedCapital ?? debt.capital ?? analyzed.awardedCapital ?? "",
      ),
      rejectedCapital: String(
        debt.rejectedCapital ?? analyzed.rejectedCapital ?? "",
      ),
      confidence: debt.confidence ?? analyzed.confidence ?? 1,
      status: debt.status || analyzed.status || "awarded",
      evidence: debt.evidence || analyzed.evidence || savedResult.evidence || [],
      rules: (debt.manualTranches || debt.rules || []).map((rule) => ({
        ...rule,
        id: crypto.randomUUID(),
        from: inputDate(rule.from),
        to: inputDate(rule.to),
      })),
    };
  });
}

export function SentenceInterestImporter({
  api,
  initialCalculation = null,
  onCalculated,
  onChanged = () => {},
  onDraftCreated = () => {},
  onDraftChange = () => {},
  onBeforeCalculate = async () => {},
  onAnalysisRetried = () => {},
}) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [cuij, setCuij] = useState("");
  const [expediente, setExpediente] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [calculationDate, setCalculationDate] = useState(todayInput());
  const [cutoffDate, setCutoffDate] = useState(todayInput());
  const [dragging, setDragging] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [debts, setDebts] = useState([]);
  const [clarification, setClarification] = useState("");
  const [reconsiderationOpen, setReconsiderationOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");
  const draftChangedRef = useRef(false);

  useEffect(() => {
    if (!initialCalculation) return;
    const analyzed = initialCalculation.analysis?.data || null;
    const appliedClarification = initialCalculation.analysis?.clarification || "";
    const useReconsideredAnalysis = Boolean(appliedClarification)
      && Number(initialCalculation.version || 0) <= Number(
        initialCalculation.analysis?.clarificationBaseVersion ?? -1,
      );
    const editableInput = initialCalculation.draftInput
      || (useReconsideredAnalysis ? null : initialCalculation.input)
      || null;
    setClarification(appliedClarification);
    setReconsiderationOpen(false);
    if (!editableInput && analyzed) {
      setCalculationDate(analyzed.firstInstanceJudgmentDate || analyzed.calculationDate || "");
      setCutoffDate(analyzed.suggestedCutoffDate || todayInput());
      setAnalysis(analyzed);
      setDebts(analysisDebts(analyzed));
    } else if (editableInput) {
      const editableAnalysis = analyzed || {
        case: editableInput.case || initialCalculation.result?.case || {},
        decisions: [],
        warnings: [],
        requiresFirstInstanceJudgmentDate: true,
        firstInstanceJudgmentDate: editableInput.calculationDate || "",
        finalJudgmentDate: null,
      };
      setCalculationDate(editableInput.calculationDate || "");
      setCutoffDate(editableInput.cutoffDate || todayInput());
      setAnalysis({
        ...editableAnalysis,
        case: editableInput.case || editableAnalysis.case || {},
      });
      setDebts(savedSentenceDebts(
        editableInput,
        editableAnalysis,
        initialCalculation.result,
      ));
    } else {
      setAnalysis(null);
      setDebts([]);
    }
    draftChangedRef.current = false;
    setError("");
  }, [initialCalculation]);

  const selectedCount = useMemo(
    () => debts.reduce((count, debt) => (
      count
      + (debt.includeAwarded ? 1 : 0)
      + (debt.includeRejected ? 1 : 0)
    ), 0),
    [debts],
  );
  const selectedSourceCount = selectedDocumentIds.length + files.length;

  useEffect(() => {
    if (!draftChangedRef.current || !initialCalculation || !analysis) return;
    draftChangedRef.current = false;
    onDraftChange({
      source: "sentences",
      calculationDate,
      cutoffDate,
      case: analysis.case || {},
      debts: debts.map((debt) => ({
        id: debt.id,
        label: debt.label,
        claimedCapital: debt.claimedCapital,
        awardedCapital: debt.awardedCapital,
        rejectedCapital: debt.rejectedCapital,
        includeAwarded: debt.includeAwarded,
        includeRejected: debt.includeRejected,
        confidence: debt.confidence,
        status: debt.status,
        evidence: debt.evidence || [],
        manualTranches: debt.rules.map((rule) => ({
          calculationType: rule.calculationType,
          ...(rule.calculationType === "rate"
            ? { rateCode: Number(rule.rateCode) }
            : {}),
          from: apiDate(rule.from),
          to: apiDate(rule.to),
        })),
      })),
    });
  }, [
    analysis,
    calculationDate,
    cutoffDate,
    debts,
    initialCalculation,
    onDraftChange,
  ]);

  function addFiles(incoming) {
    const candidates = Array.from(incoming || []);
    const pdfs = candidates.filter((file) => (
      file.type === "application/pdf"
      || file.name.toLocaleLowerCase().endsWith(".pdf")
    ));
    if (pdfs.length !== candidates.length) {
      setError("Solo podés adjuntar archivos PDF.");
      return;
    }
    if (selectedSourceCount + pdfs.length > MAX_SENTENCE_FILES) {
      setError("Podés analizar hasta tres resoluciones en total.");
      return;
    }
    const totalBytes = [...files, ...pdfs].reduce(
      (sum, file) => sum + file.size,
      0,
    );
    if (totalBytes > MAX_SENTENCE_BYTES) {
      setError("Los PDFs subidos no pueden superar 15 MB en total.");
      return;
    }
    setFiles((current) => [...current, ...pdfs]);
    setAnalysis(null);
    setDebts([]);
    setError("");
  }

  async function loadDocuments(event) {
    event?.preventDefault();
    const normalizedCuij = cuij.trim();
    if (!CUIJ_PATTERN.test(normalizedCuij)) {
      setError(
        "Ingresá un CUIJ con formato 00-00000000-0 o 00-00000000-0/1.",
      );
      return;
    }
    setLoadingDocuments(true);
    setError("");
    try {
      const result = await api.listJudicialInterestSentences(normalizedCuij);
      const identified = result.items || [];
      setExpediente(result.expediente || { cuij: normalizedCuij });
      setDocuments(identified);
      setSelectedDocumentIds(
        identified
          .slice(0, Math.max(0, MAX_SENTENCE_FILES - files.length))
          .map(({ actId }) => actId),
      );
    } catch (requestError) {
      setExpediente(null);
      setDocuments([]);
      setSelectedDocumentIds([]);
      setError(requestError.message);
    } finally {
      setLoadingDocuments(false);
    }
  }

  function updateDebt(id, changes) {
    draftChangedRef.current = true;
    onChanged();
    setDebts((current) =>
      current.map((debt) => debt.id === id ? { ...debt, ...changes } : debt));
  }

  function updateRule(debtId, ruleId, changes) {
    draftChangedRef.current = true;
    onChanged();
    setDebts((current) => current.map((debt) => {
      if (debt.id !== debtId) return debt;
      const changedIndex = debt.rules.findIndex(({ id }) => id === ruleId);
      return {
        ...debt,
        rules: debt.rules.map((rule, index) => {
          if (index === changedIndex) return { ...rule, ...changes };
          if (changes.to !== undefined && index === changedIndex + 1) {
            return { ...rule, from: nextIsoDate(changes.to) };
          }
          return rule;
        }),
      };
    }));
  }

  async function analyze() {
    const selectedDocuments = documents
      .filter(({ actId }) => selectedDocumentIds.includes(actId))
      .map((document) => ({
        cuij: expediente?.cuij || cuij.trim(),
        actId: document.actId,
        title: document.title,
        date: document.date || null,
      }));
    if (!selectedDocuments.length && !files.length) {
      setError("Elegí o subí al menos una resolución.");
      return;
    }
    setAnalyzing(true);
    setError("");
    try {
      const calculation = await api.createSentenceJudicialInterestDraft(
        files,
        todayInput(),
        selectedDocuments,
      );
      onDraftCreated(calculation);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAnalyzing(false);
    }
  }

  async function retryAnalysis(nextClarification) {
    if (!initialCalculation) return;
    setAnalyzing(true);
    setError("");
    try {
      await onBeforeCalculate();
      onAnalysisRetried(
        await api.retryJudicialInterestAnalysis(
          initialCalculation.id,
          nextClarification,
        ),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setAnalyzing(false);
    }
  }

  async function calculate() {
    const selected = debts.filter(({ includeAwarded, includeRejected }) => (
      includeAwarded || includeRejected
    ));
    if (!selectedCount) {
      setError("Seleccioná al menos un rubro para calcular.");
      return;
    }
    const invalid = selected.find(
      (debt) =>
        (
          debt.includeAwarded
          && !(Number(debt.awardedCapital) > 0)
        )
        || (
          debt.includeRejected
          && !(Number(debt.rejectedCapital) > 0)
        )
        || debt.rules.some((rule) =>
          !rule.from
          || !rule.to
          || rule.to <= rule.from
          || !rule.calculationType
          || (
            rule.calculationType === "rate"
            && !Number.isInteger(Number(rule.rateCode))
          )),
    );
    if (invalid) {
      setError(`Revisá el capital y los períodos de “${invalid.label}”.`);
      return;
    }
    if (!calculationDate) {
      setError("Ingresá la fecha de sentencia de primera instancia.");
      return;
    }
    if (!cutoffDate || cutoffDate <= calculationDate) {
      setError("La fecha de corte debe ser posterior a la sentencia de primera instancia.");
      return;
    }
    setCalculating(true);
    setError("");
    try {
      await onBeforeCalculate();
      const input = {
        source: "sentences",
        calculationDate,
        cutoffDate,
        case: analysis.case,
        outcomes: [
          {
            key: "awarded",
            label: "Por lo que prospera",
            debts: selected.filter(({ includeAwarded }) => includeAwarded)
              .map((debt) => outcomeDebt(debt, debt.awardedCapital)),
          },
          {
            key: "rejected",
            label: "Por lo que rechaza",
            debts: selected.filter(({ includeRejected }) => includeRejected)
              .map((debt) => outcomeDebt(debt, debt.rejectedCapital)),
          },
        ].filter(({ debts: outcomeDebts }) => outcomeDebts.length),
      };
      const calculation = initialCalculation
        ? await api.recalculateJudicialInterestBatch(initialCalculation.id, input)
        : await api.calculateJudicialInterestBatch(input);
      onCalculated(calculation);
      window.requestAnimationFrame(() => {
        document.querySelector(".interest-sentence-result-overview")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCalculating(false);
    }
  }

  const analysisStatus = initialCalculation?.analysis?.status;
  if (
    initialCalculation
    && ["preparing", "queued", "processing"].includes(analysisStatus)
  ) {
    return (
      <div className="sentence-interest-importer">
        <section className="form-card sentence-analysis-progress" aria-live="polite">
          <LoaderCircle className="spin" size={24} />
          <div>
            <span className="eyebrow">Análisis en curso</span>
            <h2>Estamos procesando las sentencias</h2>
            <p>
              Podés volver al historial o cerrar esta página. El análisis seguirá
              ejecutándose y vas a poder retomarlo desde este mismo cálculo.
            </p>
            {initialCalculation.analysis.files?.length > 0 && (
              <small>
                {initialCalculation.analysis.files
                  .map(({ fileName }) => fileName)
                  .join(" · ")}
              </small>
            )}
          </div>
        </section>
      </div>
    );
  }

  if (initialCalculation && analysisStatus === "failed") {
    return (
      <div className="sentence-interest-importer">
        <FeedbackBanner tone="error">
          {initialCalculation.analysis.error?.message
            || "No pudimos analizar las sentencias."}
        </FeedbackBanner>
        <section className="form-card sentence-analysis-progress is-failed">
          <AlertTriangle size={24} />
          <div>
            <h2>El análisis necesita reintentarse</h2>
            <p>Los PDFs siguen guardados; no hace falta volver a subirlos.</p>
            <button
              className="primary-button"
              type="button"
              disabled={analyzing}
              onClick={() => retryAnalysis()}
            >
              {analyzing
                ? <LoaderCircle className="spin" size={18} />
                : <Sparkles size={18} />}
              {analyzing ? "Reintentando…" : "Reintentar análisis"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="sentence-interest-importer">
      {error && <FeedbackBanner tone="error">{error}</FeedbackBanner>}

      {!initialCalculation && (
      <section className="form-card sentence-upload-card">
        <div className="form-card-title">
          <span className="sentence-ai-icon"><Sparkles size={20} /></span>
          <div>
            <h2>Preparar desde sentencias</h2>
            <p>
              Ingresá el CUIJ para traer las resoluciones identificadas o subí
              tus propios PDF. Podés combinar hasta tres documentos del mismo
              expediente.
            </p>
          </div>
        </div>

        <div className="sentence-source-grid">
          <form className="sentence-cuij-source" onSubmit={loadDocuments}>
            <span className="sentence-source-icon"><Search size={21} /></span>
            <div>
              <strong>Buscar por CUIJ</strong>
              <span>Traé las resoluciones detectadas por el Poder Judicial.</span>
            </div>
            <label htmlFor="interest-sentence-cuij">
              <span>CUIJ del expediente</span>
              <input
                id="interest-sentence-cuij"
                value={cuij}
                placeholder="13-12345678-9"
                autoComplete="off"
                inputMode="numeric"
                disabled={loadingDocuments}
                onChange={(event) => {
                  setCuij(formatCuij(event.target.value));
                  setExpediente(null);
                  setDocuments([]);
                  setSelectedDocumentIds([]);
                  setError("");
                }}
              />
            </label>
            <button
              className="secondary-button"
              type="submit"
              disabled={loadingDocuments || !cuij.trim()}
            >
              {loadingDocuments
                ? <LoaderCircle className="spin" size={18} />
                : <Search size={18} />}
              {loadingDocuments ? "Buscando…" : "Buscar documentos"}
            </button>
          </form>

          <button
            className={`sentence-drop-zone ${dragging ? "is-dragging" : ""}`}
            type="button"
            disabled={selectedSourceCount >= MAX_SENTENCE_FILES}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles([...event.dataTransfer.files]);
            }}
          >
            <Upload size={23} />
            <strong>Subir PDF manualmente</strong>
            <span>
              También podés combinar archivos propios con los documentos
              identificados.
            </span>
          </button>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept="application/pdf,.pdf"
            multiple
            onChange={(event) => {
              addFiles([...event.target.files]);
              event.target.value = "";
            }}
          />

        </div>

        {expediente && (
          <div className="sentence-identified-documents">
            <header>
              <div>
                <span className="eyebrow">Documentos identificados</span>
                <strong>
                  {expediente.caratula || expediente.cuij || cuij}
                </strong>
                {expediente.caratula && (
                  <small>{expediente.cuij || cuij}</small>
                )}
              </div>
              <span>
                {documents.length} {documents.length === 1
                  ? "resolución"
                  : "resoluciones"}
              </span>
            </header>

            {documents.length > 0 ? (
              <div className="sentence-identified-list">
                {documents.map((document) => {
                  const checked = selectedDocumentIds.includes(document.actId);
                  return (
                    <label key={document.actId}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked
                          && selectedSourceCount >= MAX_SENTENCE_FILES}
                        onChange={(event) => {
                          setSelectedDocumentIds((current) => (
                            event.target.checked
                              ? [...current, document.actId]
                              : current.filter((id) => id !== document.actId)
                          ));
                          setError("");
                        }}
                      />
                      <FileText size={17} />
                      <span>
                        <strong>{document.title}</strong>
                        <small>{displayDocumentDate(document.date)}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="sentence-no-documents">
                No encontramos resoluciones identificadas para este CUIJ.
                Podés subir los PDFs manualmente.
              </p>
            )}
          </div>
        )}

        {files.length > 0 && (
          <div className="sentence-file-list">
            {files.map((file, index) => (
              <span key={`${file.name}-${file.size}-${index}`}>
                <FileText size={15} />
                <strong>{file.name}</strong>
                <small>{(file.size / 1024 / 1024).toFixed(2)} MB</small>
                <button
                  type="button"
                  aria-label={`Quitar ${file.name}`}
                  onClick={() => {
                    setFiles((current) =>
                      current.filter((_candidate, position) => position !== index));
                    setAnalysis(null);
                    setDebts([]);
                  }}
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        <button
          className="primary-button"
          type="button"
          disabled={analyzing || !selectedSourceCount}
          onClick={analyze}
        >
          {analyzing
            ? <LoaderCircle className="spin" size={18} />
            : <FileSearch size={18} />}
          {analyzing ? "Preparando documentos…" : "Guardar y analizar sentencias"}
        </button>
      </section>
      )}

      {analysis && (
        <section className="sentence-analysis-section">
          <header>
            <div>
              <span className="eyebrow">Revisión humana</span>
              <h2>Rubros identificados</h2>
              <p>
                {analysis.case?.cuij || "CUIJ no identificado"}
                {analysis.case?.title ? ` · ${analysis.case.title}` : ""}
              </p>
            </div>
            <span className="sentence-selected-count">
              {selectedCount} {selectedCount === 1
                ? "importe incluido"
                : "importes incluidos"}
            </span>
          </header>

          <div className="sentence-reconsideration">
            <div className="sentence-reconsideration-heading">
              <MessageSquareText size={19} />
              <span>
                <strong>
                  {initialCalculation?.analysis?.clarification
                    ? "Aclaración aplicada al análisis"
                    : "¿Interpretás algún punto de otra manera?"}
                </strong>
                <small>
                  {initialCalculation?.analysis?.clarification
                    ? "Podés editarla y pedir una nueva lectura de las sentencias."
                    : "Agregá una aclaración profesional y Justiniano volverá a leer las sentencias teniéndola en cuenta."}
                </small>
              </span>
              <button
                className="secondary-button"
                type="button"
                aria-expanded={reconsiderationOpen}
                aria-controls="sentence-reconsideration-form"
                onClick={() => setReconsiderationOpen((open) => !open)}
              >
                {reconsiderationOpen
                  ? "Cerrar"
                  : initialCalculation?.analysis?.clarification
                    ? "Editar aclaración"
                    : "Aclarar o reconsiderar"}
              </button>
            </div>
            {reconsiderationOpen && (
              <form
                id="sentence-reconsideration-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const value = clarification.trim();
                  if (!value) {
                    setError("Escribí una aclaración para volver a analizar las sentencias.");
                    return;
                  }
                  retryAnalysis(value);
                }}
              >
                <label htmlFor="sentence-analysis-clarification">
                  <span>Aclaración o criterio a reconsiderar</span>
                  <textarea
                    id="sentence-analysis-clarification"
                    value={clarification}
                    maxLength={4_000}
                    rows={4}
                    autoFocus
                    disabled={analyzing}
                    placeholder="Ej.: La Cámara modificó solamente el rubro incapacidad; el daño moral quedó confirmado con el capital de primera instancia."
                    onChange={(event) => {
                      setClarification(event.target.value);
                      setError("");
                    }}
                  />
                </label>
                <div>
                  <small>{clarification.length.toLocaleString("es-AR")} / 4.000</small>
                  <button
                    className="primary-button"
                    type="submit"
                    disabled={analyzing || !clarification.trim()}
                  >
                    {analyzing
                      ? <LoaderCircle className="spin" size={18} />
                      : <Sparkles size={18} />}
                    {analyzing
                      ? "Volviendo a analizar…"
                      : "Volver a analizar con esta aclaración"}
                  </button>
                </div>
              </form>
            )}
          </div>

          {analysis.decisions?.length > 0 && (
            <div className="sentence-decisions">
              {analysis.decisions.map((decision) => (
                <span key={`${decision.documentIndex}-${decision.instance}`}>
                  <CheckCircle2 size={16} />
                  <strong>{decision.summary}</strong>
                  <small>{decision.decisionDate || "Fecha no identificada"}</small>
                </span>
              ))}
            </div>
          )}

          {analysis.requiresFirstInstanceJudgmentDate ? (
            <label className="sentence-calculation-date">
              <span>Fecha de sentencia de primera instancia</span>
              <ArgentineDateInput
                value={calculationDate}
                onChange={(value) => {
                  draftChangedRef.current = true;
                  onChanged();
                  const previous = calculationDate;
                  setCalculationDate(value);
                  setDebts((current) => current.map((debt) => ({
                    ...debt,
                    rules: debt.rules.map((rule) => ({
                      ...rule,
                      to: rule.to === previous ? value : rule.to,
                    })),
                  })));
                }}
                required
              />
            <small>
              {initialCalculation
                ? "Podés cambiarla para generar una nueva versión del cálculo."
                : "No pudimos identificarla con seguridad en los documentos."}
            </small>
            </label>
          ) : (
            <p className="sentence-final-date">
              <CheckCircle2 size={16} />
              Los intereses se calculan hasta la sentencia de primera instancia del{" "}
              <strong>{apiDate(analysis.firstInstanceJudgmentDate)}</strong>.
            </p>
          )}

          <label className="sentence-cutoff-date">
            <span>Fecha de corte</span>
            <ArgentineDateInput
              value={cutoffDate}
              onChange={(value) => {
                draftChangedRef.current = true;
                onChanged();
                setCutoffDate(value);
              }}
              required
            />
            <small>
              Proyectaremos como deuda de dinero el capital total desde la
              sentencia de primera instancia hasta esta fecha.
              {analysis.finalJudgmentDate
                ? ` Sugerida: 60 días desde la sentencia final del ${apiDate(analysis.finalJudgmentDate)}.`
                : " No identificamos una sentencia final; usamos la fecha actual."}
            </small>
          </label>

          {analysis.warnings?.length > 0 && (
            <div className="sentence-analysis-warnings">
              {analysis.warnings.map((warning) => (
                <p key={`${warning.code}-${warning.message}`}>
                  <AlertTriangle size={15} />
                  {warning.message}
                </p>
              ))}
            </div>
          )}

          <div className="sentence-debt-list">
            {debts.map((debt) => (
              <article
                className={`sentence-debt-card ${
                  debt.includeAwarded || debt.includeRejected ? "is-selected" : ""
                }`}
                key={debt.id}
              >
                <header>
                  <input
                    className="sentence-debt-label"
                    value={debt.label}
                    onChange={(event) =>
                      updateDebt(debt.id, { label: event.target.value })}
                  />
                  <span className={`sentence-confidence ${
                    debt.confidence >= 0.8 ? "is-high" : "is-review"
                  }`}>
                    {Math.round(debt.confidence * 100)}% confianza
                  </span>
                </header>

                <div className="sentence-debt-fields">
                  <label>
                    <span>Monto reclamado</span>
                    <div className="money-input">
                      <span>$</span>
                      <input
                        inputMode="decimal"
                        value={debt.claimedCapital}
                        onChange={(event) =>
                          updateDebt(debt.id, {
                            claimedCapital: event.target.value,
                          })}
                      />
                    </div>
                  </label>
                  <label>
                    <span>Por lo que prospera</span>
                    <div className="money-input">
                      <span>$</span>
                      <input
                        inputMode="decimal"
                        value={debt.awardedCapital}
                        onChange={(event) => updateDebt(debt.id, {
                          awardedCapital: event.target.value,
                        })}
                      />
                    </div>
                  </label>
                  <label>
                    <span>Por lo que rechaza</span>
                    <div className="money-input">
                      <span>$</span>
                      <input
                        inputMode="decimal"
                        value={debt.rejectedCapital}
                        onChange={(event) => updateDebt(debt.id, {
                          rejectedCapital: event.target.value,
                        })}
                      />
                    </div>
                  </label>
                </div>

                <div className="sentence-outcome-selectors">
                  <label>
                    <input
                      type="checkbox"
                      checked={debt.includeAwarded}
                      disabled={!(Number(debt.awardedCapital) > 0)}
                      onChange={(event) => updateDebt(debt.id, {
                        includeAwarded: event.target.checked,
                      })}
                    />
                    Incluir en “Por lo que prospera”
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={debt.includeRejected}
                      disabled={!(Number(debt.rejectedCapital) > 0)}
                      onChange={(event) => updateDebt(debt.id, {
                        includeRejected: event.target.checked,
                      })}
                    />
                    Incluir en “Por lo que rechaza”
                  </label>
                  <span>
                    Cuantificado al {debt.quantificationDate || "sin identificar"}
                  </span>
                </div>

                <div className="sentence-rule-list">
                  {debt.rules.map((rule, index) => (
                    <div className="sentence-rule-row" key={rule.id}>
                      <span className="tranche-number">{index + 1}</span>
                      <select
                        value={optionValue(rule)}
                        onChange={(event) => {
                          const value = event.target.value;
                          updateRule(debt.id, rule.id, value.startsWith("rate:")
                            ? {
                                calculationType: "rate",
                                rateCode: Number(value.slice(5)),
                              }
                            : { calculationType: value, rateCode: null });
                        }}
                      >
                        <option value="value-debt">Deuda de valor</option>
                        <option value="money-debt">Deuda de dinero</option>
                        {Object.entries(RATE_NAMES).map(([code, name]) => (
                          <option value={`rate:${code}`} key={code}>{name}</option>
                        ))}
                      </select>
                      <ArgentineDateInput
                        value={rule.from}
                        onChange={(value) =>
                          updateRule(debt.id, rule.id, { from: value })}
                      />
                      <span className="sentence-rule-arrow">hasta</span>
                      <ArgentineDateInput
                        value={rule.to}
                        onChange={(value) =>
                          updateRule(debt.id, rule.id, { to: value })}
                      />
                      <button
                        type="button"
                        aria-label="Eliminar período"
                        onClick={() => updateDebt(debt.id, {
                          rules: debt.rules.filter(({ id }) => id !== rule.id),
                        })}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  <button
                    className="sentence-add-rule"
                    type="button"
                    onClick={() => updateDebt(debt.id, {
                      rules: [
                        ...debt.rules,
                        {
                          id: crypto.randomUUID(),
                          calculationType: "rate",
                          rateCode: 5,
                          from:
                            nextIsoDate(debt.rules.at(-1)?.to)
                            || debt.interestStartDate
                            || "",
                          to: calculationDate,
                          rationale: "Agregado manualmente",
                        },
                      ],
                    })}
                  >
                    <Plus size={14} />
                    Agregar período
                  </button>
                </div>

                {debt.evidence?.length > 0 && (
                  <details className="sentence-debt-evidence">
                    <summary>Ver evidencia de la sentencia</summary>
                    {debt.evidence.map((evidence, index) => (
                      <blockquote key={`${evidence.documentIndex}-${evidence.page}-${index}`}>
                        “{evidence.quote}”
                        <footer>
                          Documento {evidence.documentIndex}, página {evidence.page}
                        </footer>
                      </blockquote>
                    ))}
                  </details>
                )}
              </article>
            ))}
          </div>

          <footer className="sentence-analysis-actions">
            <p>
              Justiniano puede equivocarse. Confirmá capitales, fechas y tasas
              antes de generar la liquidación.
            </p>
            <button
              className="primary-button"
              type="button"
              disabled={calculating || !selectedCount}
              onClick={calculate}
            >
              {calculating
                ? <LoaderCircle className="spin" size={18} />
                : <Sparkles size={18} />}
              {calculating
                ? "Calculando rubros…"
                : `${initialCalculation?.version > 0 ? "Recalcular" : "Calcular"} ${selectedCount} ${selectedCount === 1 ? "importe" : "importes"}`}
            </button>
          </footer>
        </section>
      )}
    </div>
  );
}
